import { Button, Icon, type IconName, IconTile, LinkButton } from "@iec-id/ui";
import { useState } from "react";
import type { AuthSearch } from "@/lib/auth-flow";
import { markIntroSeen } from "./intro-storage";
import { OnboardingCard } from "./onboarding-card";
import { useOnboardingT } from "./onboarding-i18n";
import { StepTitle } from "./step-title";

/**
 * Три слайда «что такое единый профиль» — декрет Д13.
 *
 * Стоят между финалом мастера и возвратом в сервис. Причина, по которой они
 * вообще нужны: человек пришёл из StudyHub или Портала, увидел незнакомый домен,
 * ввёл код и уходит обратно — и не понимает, что именно у него теперь есть.
 * Три экрана отвечают ровно на три вопроса: один аккаунт где, Баллы за что и
 * на что, IEC+ это что.
 *
 * Чего здесь НЕТ и не будет: маркетинга, цен крупным шрифтом, кнопки «купить».
 * Это последний экран регистрации, а не витрина — CLAUDE.md про тон витрины
 * («никакого маркетингового шума в личном кабинете») действует и тут.
 *
 * Слайды пропускаемы с любого экрана: человек, который спешит вернуться в
 * сервис, обязан иметь возможность это сделать одним нажатием.
 */
const SLIDES = [
	{ key: "account", icon: "UserRound" },
	{ key: "points", icon: "Coins" },
	{ key: "plus", icon: "Sparkles" },
] as const satisfies readonly { key: string; icon: IconName }[];

export interface EcosystemIntroProps {
	/**
	 * Подпись кнопки на последнем слайде: «Перейти в StudyHub» либо «Открыть
	 * IEC ID» — то же решение, что принял финальный шаг мастера.
	 */
	finishLabel: string;
	/** Уход туда, куда человек шёл. Зовётся и по «Пропустить», и с последнего слайда. */
	onFinish: () => void;
	/** Контекст входа — подвалу «войти в другой аккаунт» в карточке. */
	search: AuthSearch;
}

export function EcosystemIntro({ finishLabel, onFinish, search }: EcosystemIntroProps) {
	const t = useOnboardingT();
	const [index, setIndex] = useState(0);

	const slide = SLIDES[index] ?? SLIDES[0];
	const last = index === SLIDES.length - 1;

	const finish = () => {
		markIntroSeen();
		onFinish();
	};

	return (
		// Слайды — продолжение мастера, а не другой экран: та же зелёная полоса
		// сверху и тот же возврат со второго экрана. Отдельные точки-индикатор
		// были ВТОРЫМ способом сказать «вы здесь» в одном и том же потоке.
		<OnboardingCard
			search={search}
			stepIndex={index + 1}
			totalSteps={SLIDES.length}
			stepLabel={t("intro.progress", { current: index + 1, total: SLIDES.length })}
			progressLabel={t("intro.progressAria", { current: index + 1, total: SLIDES.length })}
			progressTestId="intro-progress"
			onBack={index > 0 ? () => setIndex(index - 1) : undefined}
			backLabel={t("actions.back")}
		>
			<IconTile size={48} className="bg-brand-soft">
				<Icon name={slide.icon} size={24} />
			</IconTile>

			<StepTitle>{t(`intro.${slide.key}.title`)}</StepTitle>

			<p className="t-body text-fg-secondary">{t(`intro.${slide.key}.body`)}</p>

			<div className="flex flex-col gap-2.5">
				<Button
					data-testid={last ? "intro-finish" : "intro-next"}
					variant="primary"
					fullWidth
					onClick={() => (last ? finish() : setIndex(index + 1))}
				>
					{last ? finishLabel : t("intro.next")}
				</Button>
				{last ? null : (
					<LinkButton
						data-testid="intro-skip"
						className="self-center text-fg-muted"
						onClick={finish}
					>
						{t("intro.skip")}
					</LinkButton>
				)}
			</div>
		</OnboardingCard>
	);
}
